import {
  devAddress,
  makeDevSignature,
  makeDevUpload,
} from "../../components/publicForm/devPrefill";

/**
 * A complete, valid set of answers for the public loan application — local dev
 * only (see devPrefill.js).
 *
 * Every field the form marks `required` is here, including the conditional ones
 * the chosen product/purpose reveal, so Continue passes on all seven steps
 * without stopping. Optional fields are populated too where they make the review
 * step and the submission PDF worth looking at.
 *
 * Built as a FUNCTION, not a constant: the signature is drawn on a canvas and
 * the photo ID is a real `File`, neither of which can be a module-level literal.
 * Calling it twice gives two independent Files, which is what you want when
 * prefilling a second time after a submit consumed the first.
 *
 * The choices are load-bearing, not arbitrary:
 *  - "Fix and Flip" is a CONSTRUCTION_PRODUCTS product, so the construction
 *    budget branch on Loan Details renders and gets filled.
 *  - "Purchase" keeps the refinance/payoff branch closed. Switch to
 *    "Refinance - Cash Out" by hand to exercise the other side; the payoff
 *    fields below are already filled for when you do.
 *  - primaryResidenceOwnership "Own" resolves firstTimeHomebuyer to false
 *    without needing hasEverOwnedPrimaryResidence.
 */
export const buildDevApplicationValues = () => ({
  // ── Step 1 · Borrower & Loan ──────────────────────────────────────────────
  firstName: "Dev",
  lastName: "Tester",
  // Bare 10 digits — RffPhoneField stores unformatted and masks for display.
  phone: "5555550123",
  emailAddress: "dev.tester@example.com",
  citizenshipStatus: "US Citizen",
  primaryAddress: devAddress(),
  // "YYYY-MM-DD", per DOB_VALUE_FORMAT — a full ISO timestamp would shift a day.
  dateOfBirth: "1985-06-15",
  maritalStatus: "Not Married",
  borrowerHasSSN: true,
  // Bare 9 digits, and structurally valid: area is not 000/666/900+, group is
  // not 00, serial is not 0000 — RffSsnField rejects all three.
  borrowerSSN: "123456789",
  loanProductType: "Fix and Flip",
  loanPurpose: "Purchase",
  expectedClosingDate: "2026-12-15",
  brokerDeal: false,
  // Both public forms collect files under the single `uploadMetadata` key —
  // RffFileUploadField hardcodes the field name and tags each entry with its own
  // docGroup/docType, which is how one array serves several upload fields.
  uploadMetadata: [
    makeDevUpload({
      fileName: "dev-photo-id.png",
      docGroup: "Borrower Documents",
      docType: "Photo Identification",
    }),
  ],

  // ── Step 2 · Background & Experience ──────────────────────────────────────
  cashLiquidity: 250000,
  otherLiquidity: 100000,
  estimatedFICO: "700-720",
  primaryResidenceOwnership: "Own",
  application_felony_conviction: false,
  application_financial_fraud_misdemeanor: false,
  bankruptcy_flag: false,
  open_liens_judgments: false,
  charge_offs: false,
  tax_liens: false,
  financial_judgements: false,
  pending_litigation: false,
  housing_events: [],
  hasExperienceWithFixFlips: true,
  // Counts are written at the form root under the category's row name, as
  // { less_than_three_years, greater_than_three_years } — see
  // EXPERIENCE_CATEGORIES in applicationFormShared.js.
  ff_sold: { less_than_three_years: 4, greater_than_three_years: 6 },
  ff_retained_as_rental: {
    less_than_three_years: 1,
    greater_than_three_years: 2,
  },
  borrowerExperienceSummary:
    "Local dev test data. Ten fix-and-flip projects over eight years, most recently a full gut renovation completed under budget.",

  // ── Step 3 · Loan Details ─────────────────────────────────────────────────
  baseLoanAmount: 450000,
  totalHoldback: 120000,
  totalConstructionBudget: 150000,
  // Only rendered for a REFINANCE_PURPOSES purpose. Harmless on a Purchase —
  // the fields aren't mounted, so nothing validates them — and already correct
  // if you switch loanPurpose by hand.
  outstandingLoanUpb: 300000,
  exisitingLender: "Example Capital Partners",
  existingLoanMaturityDate: "2027-03-01",
  existingLoanLatePayments: false,
  existingLoanInDefault: false,
  existingLoanHasUndrawnBudget: false,
  propertyListedForSale: false,
  propertyListedForSaleLastSixMonths: false,
  loanRelatedContacts: [
    {
      firstName: "Ada",
      lastName: "Appraiser",
      emailAddress: "ada.appraiser@example.com",
      phone: "5555550144",
    },
    {
      firstName: "Tom",
      lastName: "Title",
      emailAddress: "tom.title@example.com",
      phone: "5555550155",
    },
    {
      firstName: "Ivy",
      lastName: "Insurance",
      emailAddress: "ivy.insurance@example.com",
      phone: "5555550166",
    },
  ],

  // ── Step 4 · Properties ───────────────────────────────────────────────────
  subjectPropertyCount: "1",
  subjectProperties: [
    {
      address: devAddress({
        streetNumber: "742",
        streetName: "Evergreen Terrace",
        city: "Springfield",
        state: "IL",
        zip: "62704",
      }),
      propertyType: "SFR",
      asIsValue: 400000,
      arv: 625000,
      purchasePrice: 380000,
      purchaseDate: "2026-09-01",
      assignmentFees: 0,
      sellersConcession: 0,
      floodZone: false,
      residentialSqFootage: 1850,
      afterRenoSqFootage: 2100,
      annualTaxes: 6200,
      annualInsurance: 1800,
      annualFloodInsurance: 0,
      annualHoaFees: 0,
      annualOtherExpense: 0,
      // Stored as the C1–C6 code, not the description — the option list is
      // {value, label} and only the code is persisted.
      propertyConditionAtOrigination: "C4",
      ffPropertyStatus: "Average condition - requires moderate renovation",
      constructionBudget: 150000,
      financedBudgetPercent: 80,
    },
  ],

  // ── Step 5 · Borrowing Entity ─────────────────────────────────────────────
  applicantHasExistingEntity: true,
  entityName: "Dev Tester Holdings LLC",
  entityEINNumber: "12-3456789",
  applicantEntityPercentOwnership: 100,
  entityStateOfOrganization: "IL",
  entityType: "LLC",
  entityOrganizationDate: "2019-04-12",
  entityAssets: 750000,

  // ── Step 7 · Sign & Submit ────────────────────────────────────────────────
  signature: makeDevSignature("Dev Tester"),
});

export default buildDevApplicationValues;
